import { defineStore } from 'pinia'
import {
  getIndexerExplorerApi,
  getIndexerRestExplorerApi
} from '@shared/service'
import {
  peggyDepositTxToPeggyTransaction,
  peggyWithdrawalTxToPeggyTransaction
} from './pendingTransaction/utils/peggy'
import { getPeggyTheGraphService } from '@/app/services/thegraph'
import { getTxHashListFromSkipTransaction } from '@/app/utils/skip'
import { TransactionState } from '@/types'
import type { IBCTransferTx, ExplorerTransaction } from '@injectivelabs/sdk-ts'
import type { UserDeposit, PeggyTransaction } from '@/types/peggy'

type TransactionStoreState = {
  IbcCosmosTx: IBCTransferTx[]
  peggoUserDeposits: UserDeposit[]
  pendingIbcCosmosTx: IBCTransferTx[]
  latestTransactions: ExplorerTransaction[]

  peggyDepositTransactions: PeggyTransaction[]
  peggyWithdrawalTransactions: PeggyTransaction[]

  pendingPeggyDepositTransactions: PeggyTransaction[]
  pendingPeggyWithdrawalTransactions: PeggyTransaction[]
}

const initialStateFactory = (): TransactionStoreState => ({
  IbcCosmosTx: [],
  peggoUserDeposits: [],
  pendingIbcCosmosTx: [],
  latestTransactions: [],
  peggyDepositTransactions: [],
  peggyWithdrawalTransactions: [],
  pendingPeggyDepositTransactions: [],
  pendingPeggyWithdrawalTransactions: []
})

export const useTransactionStore = defineStore('transaction', {
  state: (): TransactionStoreState => initialStateFactory(),
  getters: {
    peggyTransactions: (state) => {
      return [
        ...state.peggyDepositTransactions,
        ...state.peggyWithdrawalTransactions
      ].sort((txA, txB) => txB.timestamp - txA.timestamp)
    },

    cosmosWithoutSkipTx: (state) => {
      const skipStore = useSkipStore()

      const txHashList = skipStore.transactions
        .map(getTxHashListFromSkipTransaction)
        .flat()

      return state.IbcCosmosTx.filter(
        (tx) =>
          !tx.txHashesList.some((hash) =>
            txHashList.includes(hash.toLowerCase().replace('0x', ''))
          )
      )
    }
  },
  actions: {
    async fetchTransactions() {
      const transactionStore = useTransactionStore()

      await Promise.all([
        transactionStore.fetchCosmosTransactions(),
        transactionStore.fetchPeggyDepositTransactions(),
        transactionStore.fetchPeggyWithdrawalTransactions()
      ])

      transactionStore.processPendingPeggyDepositTransactions()
    },

    async fetchLatestTransactions() {
      const transactionStore = useTransactionStore()
      const sharedWalletStore = useSharedWalletStore()
      const indexerRestExplorerApi = await getIndexerRestExplorerApi()

      const { transactions } =
        await indexerRestExplorerApi.fetchAccountTransactions({
          account: sharedWalletStore.injectiveAddress,
          params: {
            limit: 100
          }
        })

      transactionStore.$patch({
        latestTransactions: transactions
      })
    },

    async fetchCosmosTransactions() {
      const transactionStore = useTransactionStore()
      const sharedWalletStore = useSharedWalletStore()

      const indexerExplorerApi = await getIndexerExplorerApi()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const transactions = await indexerExplorerApi.fetchIBCTransferTxs({
        sender: sharedWalletStore.injectiveAddress,
        receiver: sharedWalletStore.injectiveAddress
      })

      transactionStore.$patch({
        IbcCosmosTx: transactions
      })
    },

    async fetchPeggyDepositTransactions() {
      const indexerExplorerApi = await getIndexerExplorerApi()

      const transactionStore = useTransactionStore()
      const sharedWalletStore = useSharedWalletStore()

      const transactions = await indexerExplorerApi.fetchPeggyDepositTxs({
        sender: sharedWalletStore.address,
        receiver: sharedWalletStore.injectiveAddress
      })

      const peggoUserDeposits = await getPeggyTheGraphService().getUserDeposits(
        sharedWalletStore.address
      )

      const peggyDepositTransactions = transactions.map((tx) =>
        peggyDepositTxToPeggyTransaction(tx, peggoUserDeposits)
      )

      transactionStore.$patch({
        peggyDepositTransactions,
        peggoUserDeposits
      })
    },

    async fetchPeggyWithdrawalTransactions() {
      const transactionStore = useTransactionStore()
      const sharedWalletStore = useSharedWalletStore()
      const indexerExplorerApi = await getIndexerExplorerApi()

      const transactions = await indexerExplorerApi.fetchPeggyWithdrawalTxs({
        sender: sharedWalletStore.injectiveAddress,
        receiver: sharedWalletStore.address
      })

      const peggyWithdrawalTransactions = transactions.map(
        peggyWithdrawalTxToPeggyTransaction
      )

      transactionStore.$patch({
        peggyWithdrawalTransactions
      })
    },

    addPendingPeggyDepositTransaction(transaction: PeggyTransaction) {
      const transactionStore = useTransactionStore()
      transactionStore.pendingPeggyDepositTransactions.push(transaction)
    },

    addPendingPeggyWithdrawalTransaction(transaction: PeggyTransaction) {
      const transactionStore = useTransactionStore()
      transactionStore.pendingPeggyWithdrawalTransactions.push(transaction)
    },

    addPendingIbcCosmosTx(transaction: IBCTransferTx) {
      const transactionStore = useTransactionStore()
      transactionStore.pendingIbcCosmosTx.push(transaction)
    },

    processPendingPeggyDepositTransactions() {
      const transactionStore = useTransactionStore()
      const pendingTxs = transactionStore.pendingPeggyDepositTransactions
      const depositTxs = transactionStore.peggyDepositTransactions

      const newPendingTx: PeggyTransaction[] = []

      for (const tx of pendingTxs) {
        if (
          depositTxs.find(
            (depositTx) =>
              depositTx.ethTxHash?.toLowerCase() === tx.ethTxHash?.toLowerCase()
          )
        ) {
          continue
        }

        const peggoDeposit = transactionStore.peggoUserDeposits.find(
          (deposit) => deposit.id.includes(tx.ethTxHash || '')
        )

        if (peggoDeposit) {
          newPendingTx.push({
            ...tx,
            state: TransactionState.EthereumConfirming,
            nonce: peggoDeposit.eventNonce,
            blockHeight: peggoDeposit.blockHeight
          })
        } else {
          newPendingTx.push(tx)
        }
      }

      transactionStore.pendingPeggyDepositTransactions = newPendingTx
    },

    processPendingIbcCosmosTx() {
      const transactionStore = useTransactionStore()
      const pendingTxs = transactionStore.pendingIbcCosmosTx
      const cosmosTxs = transactionStore.IbcCosmosTx

      const newPendingTx = pendingTxs.filter(
        (tx) =>
          !cosmosTxs.find(
            (cosmosTx) =>
              cosmosTx.timeoutTimestamp === tx.timeoutTimestamp &&
              cosmosTx.sender === tx.sender
          )
      )

      transactionStore.pendingIbcCosmosTx = newPendingTx
    },

    reset() {
      const transactionStore = useTransactionStore()

      const initialState = initialStateFactory()

      transactionStore.$patch({
        ...initialState
      })
    }
  }
})
